export type Enum<T> = T[keyof T]

export const ProjectTypes = {
  Project: 1,
  Ticket: 2,
  Maintenance: 3
} as const
export type ProjectType = Enum<typeof ProjectTypes>

export const projectTypeName = (type: ProjectType): string => {
  return {
    [ProjectTypes.Project]: 'Project',
    [ProjectTypes.Ticket]: 'Service Ticket',
    [ProjectTypes.Maintenance]: 'Maintenance Job'
  }[type]
}

export const projectTypeOptions = Object.entries(ProjectTypes).map(([_, value]) => ({
  label: projectTypeName(value as ProjectType),
  value
}));

// Service Tickets is being sunset. Every job-type picker gates on the same rule, so it lives
// here next to projectTypeOptions rather than being re-derived per screen; at full removal
// this and ProjectTypes.Ticket are the two things to delete.
export const projectTypeOptionsFor = (showServiceTickets: boolean) =>
  showServiceTickets
    ? projectTypeOptions
    : projectTypeOptions.filter(option => option.value !== ProjectTypes.Ticket)

// Filter state is persisted per user, so a tenant that has since lost Service Tickets can
// rehydrate a saved Ticket selection. Left alone it filters the view down to a type the
// tenant no longer has, emptying it with no visible cause.
// projectTypeIds is optional because useSavedState replaces state with the parsed blob and
// never merges defaults, so a persisted filter written before the key existed arrives undefined.
export const withoutUnavailableProjectTypes = (projectTypeIds: number[] | undefined, showServiceTickets: boolean): number[] => {
  const ids = projectTypeIds ?? []
  return showServiceTickets
    ? ids
    : ids.filter(id => id !== ProjectTypes.Ticket)
}

export const PricingTypes = {
  TimeAndMaterials: 0,
  QuotedPrice: 1,
  FlatRate: 2,
  Unbillable: 3,
  PerVisit: 4,
}
export type PricingType = Enum<typeof PricingTypes>

export const pricingTypeName = (type: PricingType): string => {
  return {
    [PricingTypes.TimeAndMaterials]: 'Time and Materials',
    [PricingTypes.QuotedPrice]: 'Quoted Price',
    [PricingTypes.FlatRate]: 'Flat Rate',
    [PricingTypes.Unbillable]: 'Unbillable',
    [PricingTypes.PerVisit]: 'Per Visit'
  }[type]
}

export const ProjectStatuses = {
  Unscheduled: 0,
  Scheduled: 1,
  WaitingOnParts: 2,
  Completed: 3
} as const
export type ProjectStatus = Enum<typeof ProjectStatuses>

export const projectStatusName = (projectStatus: ProjectStatus): string => {
  return {
    [ProjectStatuses.Unscheduled]: 'Unscheduled',
    [ProjectStatuses.Scheduled]: 'Scheduled',
    [ProjectStatuses.WaitingOnParts]: 'Waiting on Parts',
    [ProjectStatuses.Completed]: 'Completed',
  }[projectStatus]
}

export const VisitWorkareaStatuses = {
  Unscheduled: 0, // deprecated, kept for backwards compatibility
  InProgress: 1, // formerly Scheduled
  Skipped: 2, // formerly WaitingOnParts
  Completed: 3,
  CompletedUnbillable: 4,
  SkippedReschedule: 5,
} as const
export type VisitWorkareaStatus = Enum<typeof VisitWorkareaStatuses>

export const visitWorkareaStatusName = (visitWorkareaStatus: VisitWorkareaStatus): string => {
  return {
    [VisitWorkareaStatuses.Unscheduled]: '',
    [VisitWorkareaStatuses.InProgress]: 'In Progress',
    [VisitWorkareaStatuses.Skipped]: 'Skipped',
    [VisitWorkareaStatuses.Completed]: 'Completed',
    [VisitWorkareaStatuses.CompletedUnbillable]: 'Completed (Unbillable)',
    [VisitWorkareaStatuses.SkippedReschedule]: 'Skipped (Reschedule)',
  }[visitWorkareaStatus]
}

export const EstimateStatuses = {
  Unsent: 0,
  Sent: 1,
  Approved: 2,
  Lost: 3
} as const
export type EstimateStatus = Enum<typeof EstimateStatuses>

export const estimateStatusName = (estimateStatus: EstimateStatus): string => {
  return {
    [EstimateStatuses.Unsent]: 'Unsent',
    [EstimateStatuses.Sent]: 'Sent',
    [EstimateStatuses.Approved]: 'Approved',
    [EstimateStatuses.Lost]: 'Lost'
  }[estimateStatus]
}

export const calendarSchedulingCadenceText = {
  notSet: 'No recurring schedule set yet. Please set the recurring schedule.',
  needsReset: 'Please reset the recurring schedule to see the scheduling cadence.'
}

export const ItemClasses = {
  Labor: 0,
  Equipment: 1,
  Materials: 2,
  Subcontracting: 3,
  JobActivity: 4,
  Other: 99
} as const
export type ItemClass = Enum<typeof ItemClasses>

export const itemClassName = (itemClass: ItemClass): string => {
  return {
    [ItemClasses.Labor]: 'Labor',
    [ItemClasses.Equipment]: 'Equipment',
    [ItemClasses.Materials]: 'Materials',
    [ItemClasses.Subcontracting]: 'Subcontracting',
    [ItemClasses.JobActivity]: 'Job Activity',
    [ItemClasses.Other]: 'Other'
  }[itemClass]
}

export const ActualItemOrigins = {
  Timecard: 10,
  Web: 20,
  Receipt: 30,
} as const
export type ActualItemOrigin = Enum<typeof ActualItemOrigins>

export const actualItemOriginName = (origin: ActualItemOrigin): string => {
  return {
    [ActualItemOrigins.Timecard]: 'Timecard',
    [ActualItemOrigins.Web]: 'Web',
    [ActualItemOrigins.Receipt]: 'Receipt',
  }[origin]
}

export const WorkareaItemTypes = {
  Estimated: 'estimated',
  Actual: 'actual'
} as const
export type WorkareaItemType = Enum<typeof WorkareaItemTypes>

export const EmailTemplateTypes = {
  Consultation: 0,
  Proposal: 1,
  InvoiceReminder: 2
} as const
export type EmailTemplateType = Enum<typeof EmailTemplateTypes>

export const TimecardInternalNotesInputModes = {
  ShowButNotRequireInput: 1,
  ShowAndRequireInput: 2,
  ShowAndRequireOnlyWhenPricingOverridesHaveBeenMade: 3,
  Hide: 4
} as const
export type TimecardInternalNotesInputMode = Enum<typeof TimecardInternalNotesInputModes>

export const TimecardClientNotesInputModes = {
  ShowAndNotificationOffByDefault: 1,
  ShowAndNotificationOnByDefault: 2,
  ShowAndRequiredNotificationOnByDefault: 3,
  Hide: 4
} as const
export type TimecardClientNotesInputMode = Enum<typeof TimecardClientNotesInputModes>
